/**
 * <BuyBoxMatchesPanel leadId={id} />
 *
 * Community Layer 3 — "Marketplace" surface on a saved lead's report.
 * Lists every active *named* buy-box (across the workspace) that matches
 * this lead. Distinct from `<MatchedBuyersPanel/>` which uses the legacy
 * buyer-search engine; this one walks `db.buy_boxes` (Layer 3) so a single
 * Cash Buyer can have multiple named boxes ("DFW SFH < 200k", "Houston
 * multi-family") and each is matched independently.
 *
 * Endpoint:  GET /api/leads/{id}/buy-box-matches
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Target, Users, ChevronRight, Sparkles, Bell } from "lucide-react";
import { api } from "@/lib/api";

export default function BuyBoxMatchesPanel({ leadId }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [notif, setNotif] = useState(null);

  useEffect(() => {
    if (!leadId) return;
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const [{ data }, notifRes] = await Promise.all([
          api.get(`/leads/${leadId}/buy-box-matches`),
          api.get(`/leads/${leadId}/match-notifications`).catch(() => ({ data: null })),
        ]);
        if (alive) {
          setData(data);
          setNotif(notifRes?.data || null);
        }
      } catch {
        if (alive) setData(null);
      }
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, [leadId]);

  if (loading) {
    return (
      <div className="card p-4 text-center text-sm text-[var(--ink-3)]" data-testid="buy-box-matches-loading">
        <Loader2 size={14} className="animate-spin inline mr-2" />
        Scanning buy-boxes for matches…
      </div>
    );
  }
  if (!data) return null;

  const matches = data.matches || [];
  const count = matches.length;

  return (
    <section className="card p-5" data-testid="buy-box-matches-panel">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--ink-3)]">
            Community · Layer 3
          </div>
          <h3 className="font-display font-bold text-lg flex items-center gap-2">
            <Target size={16} className="text-indigo-600" />
            Buy-boxes matching this deal
          </h3>
        </div>
        {count > 0 && (
          <div className="flex items-center gap-1.5">
            <span
              className="inline-flex items-center gap-1 text-[10px] font-extrabold px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-300"
              data-testid="buy-box-matches-count"
            >
              <Sparkles size={10} /> {count} match{count === 1 ? "" : "es"}
            </span>
            {notif?.sent_count > 0 && (
              <span
                className="inline-flex items-center gap-1 text-[10px] font-extrabold px-2 py-1 rounded-full bg-rose-100 text-rose-700 border border-rose-300"
                title="Match alerts already delivered to buyers"
                data-testid="buy-box-matches-notified-count"
              >
                <Bell size={10} /> {notif.sent_count} notified
              </span>
            )}
          </div>
        )}
      </div>

      {count === 0 ? (
        <div className="text-center py-6 text-sm text-[var(--ink-3)]" data-testid="buy-box-matches-empty">
          <Users size={28} className="mx-auto opacity-40 mb-2" />
          <p>No active buy-boxes match this deal yet.</p>
          <Link to="/v3/business/team" className="text-indigo-700 font-bold hover:underline text-xs">
            Define buy-boxes on your Cash Buyers →
          </Link>
        </div>
      ) : (
        <div className="space-y-2" data-testid="buy-box-matches-list">
          {matches.map((m) => (
            <Link
              key={m.box_id}
              to={`/app/buyers/${m.buyer_id}`}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-[var(--line)] bg-white hover:bg-[var(--bg-2)] hover:border-indigo-300 transition-colors"
              data-testid={`buy-box-match-${m.box_id}`}
            >
              <div className="shrink-0 w-9 h-9 rounded-lg bg-indigo-100 text-indigo-700 flex items-center justify-center">
                <Target size={16} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-bold text-sm truncate">{m.box_name}</div>
                <div className="text-[11px] text-[var(--ink-3)] truncate">
                  {m.buyer_name || "—"}
                </div>
              </div>
              <ChevronRight size={14} className="text-[var(--ink-3)] shrink-0" />
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}
